import React, { createContext, useEffect, useState, useContext } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { Navigate, Outlet } from "react-router";
import { CircularProgress, Container } from "@mui/material";
import { auth } from "./firebase";

interface AuthContextType {
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export const ProtectedRoute = () => {
  const { user, loading } = useAuth();

  if (loading)
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

export function useAuthenticatedUser() {
  const { user, loading } = useAuth();

  if (loading) {
    throw new Error("Authentication is still loading");
  }

  if (!user) {
    throw new Error("User is not authenticated");
  }

  // ここに到達した場合、userはnullではないことが保証される
  return user;
}
